// components/Navbar.tsx
import { Link } from 'react-scroll';
import { motion } from 'framer-motion';
import { auth } from '../firebaseConfig';
import { FaSignOutAlt } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useIsPWA } from '../hooks/useIsPWA';
import { MobileHeader } from './MobileHeader';
import { MobileBottomNav } from './MobileBottomNav';

const IconSignOut = FaSignOutAlt as any;

const navLinks = [
  { to: 'hero', label: 'Inicio' },
  { to: 'spotify', label: 'Nosotros' },
  { to: 'maps', label: 'Lugares' },
];

interface NavbarProps {
  specialTitle?: string; // Title from HeroSection
}

export const Navbar = ({ specialTitle }: NavbarProps) => {
  const [isSpotifySection, setIsSpotifySection] = useState(false);
  const isMobile = useIsMobile();
  const isPWA = useIsPWA();

  // Solo usar layout móvil cuando sea móvil Y PWA
  const useMobileLayout = isMobile && isPWA;

  // Get default greeting if no special title yet
  const getDefaultGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "¡Buenos días, hermosa!";
    if (hour < 19) return "¡Buenas tardes, linda!";
    return "¡Buenas noches, mi vida!";
  };

  const handleLogout = () => {
    auth.signOut();
  };

  useEffect(() => {
    const handleScroll = () => {
      const spotifyElement = document.getElementById('spotify');
      if (spotifyElement) {
        const rect = spotifyElement.getBoundingClientRect();
        const isInView = rect.top <= 100 && rect.bottom >= 100;
        setIsSpotifySection(isInView);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Si es móvil PWA, no renderizar nada (MobileAppLayout se encarga)
  if (useMobileLayout) {
    return null;
  }

  // Navbar desktop/navegador normal
  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 p-4"
    >
      <div className={`max-w-4xl mx-auto backdrop-blur-lg rounded-xl shadow-lg flex justify-between items-center p-2 transition-all duration-300 ${
        isSpotifySection 
        ? 'bg-black/80 border border-green-400/30' 
        : 'bg-white/20'
      }`}>
        <p className={`font-script text-2xl pl-2 transition-colors duration-300 ${
          isSpotifySection ? 'text-green-400' : 'text-secondary'
        }`}>{specialTitle || getDefaultGreeting()}</p>
        
        <div className="flex items-center gap-1 sm:gap-4">
          <ul className="flex items-center gap-0 sm:gap-1">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  spy={true}
                  smooth={true}
                  offset={0}
                  duration={500}
                  className={`cursor-pointer text-sm sm:text-base font-semibold transition-colors px-3 py-2 rounded-lg ${
                    isSpotifySection 
                    ? 'text-green-400 hover:text-white' 
                    : 'text-secondary hover:text-text-light'
                  }`}
                  activeClass={`cursor-pointer text-sm sm:text-base font-semibold transition-colors px-3 py-2 rounded-lg ${
                    isSpotifySection 
                    ? 'text-green-400 hover:text-white bg-white/20' 
                    : 'text-text-main hover:text-secondary bg-white/60'
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleLogout}
            title="Cerrar Sesión"
            className={`flex items-center justify-center w-10 h-10 rounded-full shadow-md transition-all duration-300 ${
              isSpotifySection 
                ? 'bg-green-400/20 hover:bg-green-400/30 text-green-400 hover:text-green-300 border border-green-400/50' 
                : 'bg-white/60 hover:bg-white text-primary hover:text-primary'
            }`}
          >
            <IconSignOut />
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};